import React from "react";
import {
  PROJECT_DATA,
  PROJECT_COLOR_CLASSES,
  AGENT_CONFIG,
} from "../constants";

export function highlightText(
  text: string,
  query: string,
  options?: {
    activeGlobalIndex?: number;
    matchStartIndex?: number; // The starting global index for matches in this text
  }
): React.ReactElement {
  if (!query) return <>{text}</>;

  // Escape special regex characters
  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = text.split(new RegExp(`(${escapedQuery})`, "gi"));

  let localMatchCount = 0;

  return (
    <>
      {parts.map((part, i) => {
        if (part.toLowerCase() === query.toLowerCase()) {
          const currentGlobalIndex =
            (options?.matchStartIndex || 0) + localMatchCount;
          const isActiveMatch =
            currentGlobalIndex === options?.activeGlobalIndex;

          const className = isActiveMatch
            ? "bg-[#EB7138]/60 text-gray-900 font-medium px-0.5 rounded ring-2 ring-[#EB7138]/40" // Active match - orange
            : "bg-[#F6C768]/40 text-gray-900 font-medium px-0.5 rounded"; // Regular match - yellow

          localMatchCount++;

          return (
            <mark
              key={i}
              className={className}
              data-match-global-index={currentGlobalIndex}
            >
              {part}
            </mark>
          );
        } else {
          return <span key={i}>{part}</span>;
        }
      })}
    </>
  );
}

export function formatDate(date: Date): string {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Formats conversation dates with intelligent handling of date ranges and relative times
 * @param startedAt - When the conversation started (optional)
 * @param endedAt - When the conversation ended (optional)
 * @param updatedAt - When the session was last synced (fallback, optional)
 * @returns Formatted date string
 */
export function formatFullConversationDate(
  startedAt: string | undefined,
  endedAt: string | undefined,
  updatedAt?: string
): string {
  // Use endedAt as primary date, fallback to startedAt, then updatedAt
  const primaryDateStr = endedAt || startedAt || updatedAt;
  if (!primaryDateStr) return "";
  const primaryDate = new Date(primaryDateStr);

  // If we have both start and end dates, check if they span multiple days
  if (startedAt && endedAt) {
    const startDate = new Date(startedAt);
    const endDate = new Date(endedAt);

    // Check if dates are on different days
    return `From ${startDate.toLocaleString()} to ${endDate.toLocaleString()}`;
  }

  // Single day session or no start date - use relative formatting
  return primaryDate.toLocaleString();
}

/**
 * Formats conversation dates with intelligent handling of date ranges and relative times
 * @param startedAt - When the conversation started (optional)
 * @param endedAt - When the conversation ended (optional)
 * @param updatedAt - When the session was last synced (fallback, optional)
 * @returns Formatted date string
 */
export function formatConversationDate(
  startedAt: string | undefined,
  endedAt: string | undefined,
  updatedAt?: string
): string {
  // Use endedAt as primary date, fallback to startedAt, then updatedAt
  const primaryDateStr = endedAt || startedAt || updatedAt;
  if (!primaryDateStr) return "";
  const primaryDate = new Date(primaryDateStr);

  // If we have both start and end dates, check if they span multiple days
  if (startedAt && endedAt) {
    const startDate = new Date(startedAt);
    const endDate = new Date(endedAt);

    // Check if dates are on different days
    if (!isSameDay(startDate, endDate)) {
      return formatDateRange(startDate, endDate);
    }
  }

  // Single day session or no start date - use relative formatting
  return formatRelativeDate(primaryDate);
}

/**
 * Get the last date of a session
 * @param datesMap - The session to format
 * @returns Formatted date string
 */
export function lastSessionDate(session: {
  startAt?: string | number;
  endedAt?: string | number;
  updatedAt?: string | number;
  createdAt?: string | number;
}): Date {
  const dateStr =
    session.endedAt ||
    session.startAt ||
    session.updatedAt ||
    session.createdAt;
  if (dateStr === undefined) {
    return new Date();
  }
  return new Date(dateStr);
}

/**
 * Formats a session date with relative time for recent dates, to show on hover
 * @param session - The session to format
 * @returns Formatted date string
 */
export function formatFullLocalSessionDate(session: {
  startAt?: string;
  endedAt?: string;
  updatedAt?: string;
  createdAt?: string;
}): string {
  const sessionDateStr = lastSessionDate(session);
  const sessionDate = new Date(sessionDateStr);
  return sessionDate.toLocaleString();
}

/**
 * Check if two dates are on the same day
 */
function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

/**
 * Format a date range for multi-day sessions
 */
function formatDateRange(startDate: Date, endDate: Date): string {
  const now = new Date();
  const currentYear = now.getFullYear();
  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();

  // Same month and year
  if (startDate.getMonth() === endDate.getMonth() && startYear === endYear) {
    const month = startDate.toLocaleDateString("en-US", { month: "short" });
    const startDay = startDate.getDate();
    const endDay = endDate.getDate();

    if (endYear === currentYear) {
      return `${month} ${startDay} - ${endDay}`;
    } else {
      return `${month} ${startDay} - ${endDay}, ${endYear}`;
    }
  }

  // Different months, same year
  if (startYear === endYear) {
    const startMonth = startDate.toLocaleDateString("en-US", {
      month: "short",
    });
    const endMonth = endDate.toLocaleDateString("en-US", { month: "short" });
    const startDay = startDate.getDate();
    const endDay = endDate.getDate();

    if (endYear === currentYear) {
      return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
    } else {
      return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${endYear}`;
    }
  }

  // Different years
  const startFormatted = startDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const endFormatted = endDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return `${startFormatted} - ${endFormatted}`;
}

/**
 * Format a single date with relative time for recent dates
 */
function formatRelativeDate(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  // Very recent times
  if (diffMins < 1) return "just now";
  if (diffMins < 60)
    return `${diffMins} ${diffMins === 1 ? "minute" : "minutes"} ago`;
  if (diffHours < 24)
    return `${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`;

  // Days
  if (diffDays === 0) return "today";
  if (diffDays === 1) return "yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;

  // Older dates - use absolute format
  const currentYear = now.getFullYear();
  const dateYear = date.getFullYear();

  if (dateYear === currentYear) {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  } else {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
}

export function formatDateTime(date: Date): string {
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function getProjectColorClasses(colorOrProjectName: string) {
  // First check if it's a direct color name
  if (PROJECT_COLOR_CLASSES[colorOrProjectName]) {
    return PROJECT_COLOR_CLASSES[colorOrProjectName];
  }

  // Otherwise look it up by project name
  const color = PROJECT_DATA[colorOrProjectName]?.color || "gray";
  return PROJECT_COLOR_CLASSES[color] || PROJECT_COLOR_CLASSES.gray;
}

export function getToolDisplayName(tool: string): string {
  const config = AGENT_CONFIG[tool as keyof typeof AGENT_CONFIG];
  return (
    config?.displayName ||
    tool?.replace("-", " ")?.replace(/\b\w/g, (l) => l.toUpperCase()) ||
    "Unknown Tool"
  );
}

export function getToolLogo(agentName: string | undefined): string {
  const config = AGENT_CONFIG[agentName as keyof typeof AGENT_CONFIG];
  return config?.icon || AGENT_CONFIG.default.icon;
}

/**
 * Get the display name for a session with priority: userTitle > summary > name > fallback
 * @param session - Session object with name, optional userTitle, and optional metadata
 * @param fallback - Fallback text if none of the above exist (default: "Untitled Session")
 * @returns The display name following the priority order
 *
 * @example
 * // Session with user-set title (highest priority)
 * getSessionDisplayName({ name: "session-123", userTitle: "My Custom Title", metadata: { summary: "Fix login bug" } })
 * // Returns: "My Custom Title"
 *
 * @example
 * // Session with AI-generated summary only
 * getSessionDisplayName({ name: "session-123", metadata: { summary: "Fix login bug" } })
 * // Returns: "Fix login bug"
 *
 * @example
 * // Session without summary or userTitle
 * getSessionDisplayName({ name: "session-123", metadata: {} })
 * // Returns: "session-123"
 */
export function getSessionDisplayName(
  session: {
    name: string;
    userTitle?: string | null;
    metadata?: Record<string, unknown>;
  },
  fallback: string = "Untitled Session"
): string {
  // Determine the display name for a session using the following priority:
  // 1. If the user has set a custom title (userTitle), use that.
  // 2. Otherwise, if there is an AI-generated summary in metadata, use that.
  // 3. Otherwise, use the session's name.
  // 4. If none of the above are available, use the fallback value.
  const summary = session.metadata?.summary as string | undefined;
  return session.userTitle || summary || session.name || fallback;
}

/**
 * Get the display name for an exchange, prioritizing user-customized titles.
 *
 * Display priority:
 * 1. User-customized title (userTitle) - if the user has manually set a title
 * 2. First user prompt - extracted from splitExchanges (parsed markdown)
 * 3. Fallback value - if splitExchanges is empty/null (malformed markdown) or no title/prompt is found
 *
 * @param exchange - The exchange object containing userTitle, content, and splitExchanges
 * @param fallback - The fallback display name if no title or prompt is found
 * @returns The display name for the exchange
 *
 * @example
 * // Exchange with user-set title (highest priority)
 * getExchangeDisplayName({ userTitle: "My Custom Title", splitExchanges: { userMessages: [{ userMessage: "Hello" }] } })
 * // Returns: "My Custom Title"
 *
 * @example
 * // Exchange with first user prompt from splitExchanges
 * getExchangeDisplayName({ splitExchanges: { userMessages: [{ userMessage: "Hello world" }] } })
 * // Returns: "Hello world" (extracted and stripped, truncated if > 60 chars)
 *
 * @example
 * // Exchange without userTitle or splitExchanges (malformed markdown)
 * getExchangeDisplayName({ content: "malformed content" }, "Untitled Exchange")
 * // Returns: "Untitled Exchange"
 */
export function getExchangeDisplayName(
  exchange: {
    userTitle?: string | null;
    content?: string;
    splitExchanges?: {
      userMessages?: Array<{ userMessage: string }>;
    };
    metadata?: {
      summary?: string;
      summaryGeneratedAt?: string;
    };
  },
  fallback: string = "Untitled Exchange"
): string {
  // Priority 1: User-customized title
  if (exchange.userTitle) {
    return exchange.userTitle;
  }

  // Priority 2: Generated summary
  if (exchange.metadata?.summary) {
    return exchange.metadata.summary;
  }

  // Priority 3: First user prompt from splitExchanges
  if (exchange.splitExchanges?.userMessages?.[0]?.userMessage) {
    const userMessage = exchange.splitExchanges.userMessages[0].userMessage;
    // Strip HTML tags
    const text = userMessage.replace(/<[^>]*>/g, "");
    // Return up to 60 characters
    return text.length > 60 ? text.substring(0, 60) + "..." : text;
  }

  // If splitExchanges is empty/null, markdown is malformed and we can't parse it
  // Return fallback instead of trying to parse raw content with patterns that don't match the actual format
  return fallback;
}
